<template>
  <section class="sw" :style="variablesColor" :dir="IDIOMAS_RTL.includes(idiomaSolicitud) ? 'rtl' : 'ltr'"
    :lang="idiomaSolicitud">
    <div class="sw__contenedor">
      <!-- Enviada -->
      <div v-if="referencia" class="sw__exito" role="status">
        <v-icon class="sw__exito-icono">mdi-check-circle-outline</v-icon>
        <h1 class="sw__titulo">{{ t('Encargo enviado') }}</h1>
        <p class="sw__referencia">{{ t('Referencia') }} <strong>{{ referencia }}</strong></p>
        <p>
          <template v-if="exitoSinDestino">{{ t(exitoSinDestino) }}</template>
          <template v-else>{{ t('{notaria} ha recibido su encargo y le contactará en breve.', { notaria: nombreNotaria }) }}</template>
          <template v-if="conNV && !respaldo">{{ t('Recibirá un email de confirmación con el acceso a la Notaría Virtual, donde podrá seguir su estado.') }}</template>
          <template v-else-if="config.EnviarCopiaCliente && !respaldo">{{ t('Le hemos enviado un email con el resumen de los datos.') }}</template>
        </p>
        <!-- Respaldo por correo: los documentos no han podido subirse -->
        <p v-if="respaldo && docsPendientes" class="sw__exito-qr">
          <v-icon size="20">mdi-file-alert-outline</v-icon>
          {{ t('No hemos podido subir sus documentos. La notaría se los pedirá cuando le contacte.') }}
        </p>
        <!-- Llegó por el QR de la oficina: está en la notaría, que lo diga en recepción -->
        <p v-if="canalEnvio === 'qr'" class="sw__exito-qr">
          <v-icon size="20">mdi-account-tie-voice-outline</v-icon>
          {{ t('Si está en la notaría, comunique esta referencia en recepción.') }}
        </p>

        <!-- Qué pasa ahora: evita las llamadas de "¿les ha llegado?" -->
        <section class="sw__siguientes" :aria-label="t('Qué pasa ahora')">
          <p class="sw__siguientes-titulo">{{ t('Qué pasa ahora') }}</p>
          <ol>
            <li class="hecho">
              <span class="sw__siguientes-num"><v-icon size="16">mdi-check</v-icon></span>
              <div><strong>{{ t('Hemos recibido su encargo') }}</strong></div>
            </li>
            <li>
              <span class="sw__siguientes-num">2</span>
              <!-- Sin notaría de destino (minotaria, "sin preferencia"): primero se le asigna una -->
              <div v-if="siguienteSinDestino">
                <strong>{{ t(siguienteSinDestino.titulo) }}</strong>
                <span v-if="siguienteSinDestino.texto">{{ t(siguienteSinDestino.texto) }}</span>
              </div>
              <div v-else>
                <strong>{{ t('La notaría revisa sus datos y su documentación') }}</strong>
                <span v-if="plazoHoras">{{ t('Normalmente en menos de {n} horas laborables.', { n: plazoHoras }) }}</span>
              </div>
            </li>
            <li>
              <span class="sw__siguientes-num">3</span>
              <div>
                <strong>{{ textoContacto }}</strong>
                <span v-if="presupuestoEnvio">{{ t('Le enviaremos el presupuesto para que lo revise antes de empezar.') }}</span>
                <span v-else>{{ t('Para preparar el encargo y acordar la firma.') }}</span>
              </div>
            </li>
          </ol>
        </section>
        <!-- Página del trámite: seguimiento, documentación que pida la notaría, mensajes y presupuesto -->
        <div v-if="enlaceCliente" class="sw__seguimiento">
          <v-icon size="22">mdi-bookmark-outline</v-icon>
          <p>{{ t('Guarde este enlace: desde ahí podrá ver cómo va su encargo, enviar la documentación que le pidan y escribir a la notaría.') }}
            <template v-if="config.EnviarCopiaCliente">{{ t('También lo tiene en el email.') }}</template></p>
        </div>
        <div class="sw__exito-acciones">
          <v-btn v-if="enlaceCliente" :href="enlaceCliente" class="sw-btn-principal" elevation="0">
            <v-icon start>mdi-file-eye-outline</v-icon>{{ t('Ver mi encargo') }}</v-btn>
          <v-btn v-if="urlNotariaVirtual" :href="urlNotariaVirtual" target="_blank" rel="noopener"
            class="sw-btn-principal" elevation="0">{{ t('Ir a la Notaría Virtual') }}</v-btn>
          <v-btn variant="text" @click="nuevaSolicitud">{{ t('Empezar otro encargo') }}</v-btn>
        </div>
      </div>

      <template v-else>
        <header class="sw__cabecera">
          <p class="sw__antetitulo">{{ t('Encargo online') }} · {{ nombreNotaria }}</p>
          <h1 class="sw__titulo" ref="tituloRef" tabindex="-1">{{ tituloPaso }}</h1>
          <!-- Pasos con nombre: los ya hechos se pueden tocar para volver a ellos -->
          <ol class="sw__pasos" :aria-label="t('Paso {n} de {total}', { n: indice + 1, total: pasos.length })">
            <li v-for="(p, i) in pasos" :key="p"
              :class="['sw__pasos-item', { 'sw__pasos-item--hecho': i < indice, 'sw__pasos-item--actual': i === indice }]"
              :aria-current="i === indice ? 'step' : undefined">
              <button v-if="i < indice" type="button" class="sw__pasos-boton" @click="irAPaso(p)">
                <span class="sw__pasos-num"><v-icon size="14">mdi-check</v-icon></span>
                <span class="sw__pasos-nombre">{{ nombrePaso(p) }}</span>
              </button>
              <span v-else class="sw__pasos-boton">
                <span class="sw__pasos-num">{{ i + 1 }}</span>
                <span class="sw__pasos-nombre">{{ nombrePaso(p) }}</span>
              </span>
            </li>
          </ol>
          <p class="sw__paso">{{ t('Paso {n} de {total}', { n: indice + 1, total: pasos.length }) }} · {{ nombrePaso(pasoActual) }}</p>
        </header>

        <!-- Invitación de la notaría -->
        <div v-if="avisoInvitacion === 'ok' && indice > 0" class="sw__invitacion" role="status">
          <v-icon size="22">mdi-email-check-outline</v-icon>
          <p>{{ t('{notaria} ha preparado este encargo para usted. Revise sus datos y complete lo que falta.', { notaria: nombreNotaria }) }}</p>
        </div>
        <div v-else-if="avisoInvitacion === 'caducada'" class="sw__invitacion sw__invitacion--aviso" role="status">
          <v-icon size="22">mdi-link-off</v-icon>
          <p>{{ t('El enlace de la invitación ya se ha usado o ha caducado. Puede rellenar el formulario igualmente.') }}</p>
        </div>

        <!-- Se entró con un enlace a otro trámite y ya había una solicitud a medias: que elija el cliente -->
        <div v-if="tramiteEnlace" class="sw__conflicto" role="alert">
          <v-icon size="22">mdi-file-document-multiple-outline</v-icon>
          <div>
            <p>{{ t('Tiene un encargo de {tramite} a medias.', { tramite: nombreTramite(estado.tipoOperacion) }) }}</p>
            <div class="sw__conflicto-acciones">
              <v-btn variant="outlined" @click="tramiteEnlace = null">{{ t('Continuar con ella') }}</v-btn>
              <v-btn class="sw-btn-principal" elevation="0" @click="empezarConEnlace">
                {{ t('Empezar uno de {tramite}', { tramite: nombreTramite(tramiteEnlace) }) }}</v-btn>
            </div>
          </div>
        </div>

        <form class="sw__cuerpo" novalidate @submit.prevent="onContinuar">
          <SolicitudPasoPresentador v-if="pasoActual === 'presentador'" />
          <SolicitudPasoTipo v-else-if="pasoActual === 'tipo'" />
          <component :is="encargoPlantilla.pasoDestino" v-else-if="pasoActual === 'destino' && encargoPlantilla?.pasoDestino" />
          <SolicitudPasoSolicitante v-else-if="pasoActual === 'solicitante'" />
          <SolicitudPasoIntervinientes v-else-if="pasoActual === 'intervinientes'" />
          <SolicitudPasoDetalle v-else-if="pasoActual === 'detalle'" />
          <SolicitudPasoRevision v-else-if="pasoActual === 'revision'" />

          <!-- Honeypot: invisible para personas, los bots suelen rellenarlo -->
          <div class="sw__hp" aria-hidden="true">
            <label>Web <input v-model="honeypot" type="text" name="website" tabindex="-1" autocomplete="off"></label>
          </div>

          <div v-if="errorEnvio" class="sw__aviso" role="alert">
            <v-icon size="20">mdi-alert-circle-outline</v-icon>
            <span>
              {{ mensajeError }}
              <template v-if="contactoNotaria"> {{ t('También puede contactar con la notaría en') }}
                <a :href="contactoNotaria.href">{{ contactoNotaria.texto }}</a>.</template>
            </span>
          </div>

          <nav class="sw__acciones">
            <v-btn v-if="indice > 0" variant="text" prepend-icon="mdi-arrow-left" @click="anterior">{{ t('Atrás') }}</v-btn>
            <v-spacer />
            <v-btn v-if="pasoActual !== 'revision'" type="submit" class="sw-btn-principal" elevation="0"
              append-icon="mdi-arrow-right">{{ t('Continuar') }}</v-btn>
            <v-btn v-else type="submit" class="sw-btn-principal" elevation="0" :loading="enviando">
              {{ t('Enviar a la notaría') }}
            </v-btn>
          </nav>
        </form>

        <!-- Dudas por WhatsApp (si la notaría lo tiene en el chat de la web) -->
        <p v-if="whatsapp.activo.value" class="sw__whatsapp">
          <v-icon size="18">mdi-whatsapp</v-icon>
          {{ whatsapp.pregunta.value }}
          <a :href="whatsapp.enlace(whatsapp.mensaje(t('Hola, tengo una duda sobre el encargo online.')))" target="_blank" rel="noopener">{{ whatsapp.texto.value }}</a>
        </p>
        <!-- Dudas con el asistente IA de la web (solo se le pasa el trámite y el nombre del paso, nunca datos personales) -->
        <p v-if="asistenteDisponible" class="sw__asistente">
          <v-icon size="18">mdi-robot-outline</v-icon>
          {{ t('¿Dudas con este paso?') }}
          <button type="button" class="sw__asistente-boton" @click="preguntarAlAsistente">{{ t('Preguntar al asistente') }}</button>
        </p>
        <p v-if="hayDatos" class="sw__pie" :title="t('Sus datos se guardan solo en este navegador hasta que envíe el encargo.')">
          <v-icon size="16" class="sw__pie-icono">mdi-content-save-check-outline</v-icon>
          {{ t('Borrador guardado en este dispositivo') }}
          <template v-if="!confirmarBorrar">
            · <button type="button" class="sw-editar" @click="confirmarBorrar = true">{{ t('Empezar de nuevo') }}</button>
          </template>
          <span v-else class="sw__pie-confirmar" role="alert">
            {{ t('¿Borrar todo y empezar de nuevo?') }}
            <button type="button" class="sw-editar sw-editar--peligro" @click="borrarYEmpezar">{{ t('Sí, borrar') }}</button>
            <button type="button" class="sw-editar" @click="confirmarBorrar = false">{{ t('Cancelar') }}</button>
          </span>
        </p>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { IDIOMAS_RTL, idiomaSolicitud, t } from '../../composables/solicitudTextos'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from '../../store/index'
import { useStyles } from '../../store/styles'
import { ErrorEnvioSolicitud, canalValido, hayTokenDestino, tokenDestino, useSolicitudWeb } from '../../composables/useSolicitudWeb'
import { acentoLegible, estiloBotonEncargo, textoSobre } from '../../composables/solicitudColor'
import { useWhatsappNotaria } from '../../composables/solicitudWhatsapp'
import { useContactoLegal } from '../../composables/useContactoLegal'
import { PRECARGA_ENCARGO, CADUCIDAD_PRECARGA_MS, type PrecargaEncargo } from '../../composables/solicitudAcceso'
import { VueReCaptcha, useReCaptcha } from 'vue-recaptcha-v3'

const store = useStore()
const styles = useStyles()
const {
  estado, pasos, indice, pasoActual, esProfesional, config, conNV, adjuntos, tipos, elegirTipo,
  activarAutoguardado, cargarCatalogo, vigilarIdioma, borrarBorrador, validarTodo, siguiente, anterior, irAPaso, enviar, reiniciar, contarPaso,
  cargarInvitacion, encargoPlantilla, destino, videoconferenciaEn
} = useSolicitudWeb()
const route = useRoute()
const whatsapp = useWhatsappNotaria()

// --- Asistente IA: abre el chat de la web con el contexto del paso (solo trámite y paso, sin datos personales) ---
const asistenteDisponible = computed(() =>
  store.NOTARIA?.Personalizar?.ChatbotActivo === true && !store.chatbotOverQuota && !referencia.value)

function preguntarAlAsistente() {
  const paso = nombrePaso(pasoActual.value)
  const tramite = estado.value.tipoOperacion ? nombreTramite(estado.value.tipoOperacion) : ''
  store.pendingChatQuery = tramite
    ? t('Estoy rellenando mi encargo online ({tramite}) y tengo una duda sobre el paso «{paso}».', { paso, tramite })
    : t('Estoy rellenando mi encargo online y tengo una duda sobre el paso «{paso}».', { paso })
  store.isChatbotOpen = true
}

// --- Enlace directo: /tramite-online?tramite=COMPRAVENTA&origen=servicios -----------------------
/** Trámite del enlace cuando choca con una solicitud a medias de otro trámite (se pregunta al cliente). */
const tramiteEnlace = ref<string | null>(null)

function nombreTramite(codigo: string | null) {
  return tipos.value.find((x) => x.codigo === codigo)?.nombre ?? codigo ?? ''
}

function empezarConTramite(codigo: string) {
  elegirTipo(codigo)
  // Con el trámite ya elegido se empieza en el paso siguiente ("¿Para quién es el trámite?")
  if (indice.value === 0) estado.value.paso = 1
}

// --- Invitación de la notaría: /tramite-online?inv=CLAVE (trámite y datos de contacto ya puestos) --------
const avisoInvitacion = ref<'' | 'ok' | 'caducada'>('')

async function aplicarInvitacion(): Promise<boolean> {
  const token = String(route.query.inv ?? '').trim()
  if (!token) return false
  // Si vuelve con el mismo enlace, se sigue con lo que ya tenía escrito
  if (estado.value.invitacion === token) { avisoInvitacion.value = 'ok'; return true }
  const inv = await cargarInvitacion(token)
  if (!inv || !tipos.value.some((x) => x.codigo === inv.tipoOperacion)) {
    avisoInvitacion.value = 'caducada'
    return false
  }
  reiniciar()
  estado.value.invitacion = token
  estado.value.canal = 'invitacion'
  elegirTipo(inv.tipoOperacion)
  // La invitación es para el propio cliente: "para mí"
  estado.value.presentador.tipo = 'INTERESADO'
  const s = estado.value.solicitante
  s.nombre = inv.nombre || s.nombre
  s.apellidos = inv.apellidos || s.apellidos
  s.email = inv.email || s.email
  s.telefono = inv.telefono || s.telefono
  await nextTick()
  const i = pasos.value.indexOf('solicitante')
  if (i >= 0) estado.value.paso = i
  avisoInvitacion.value = 'ok'
  return true
}

function aplicarEnlace() {
  const canal = canalValido(route.query.origen)
  const codigo = String(route.query.tramite ?? '').trim().toUpperCase()
  const valido = !!codigo && tipos.value.some((x) => x.codigo === codigo)
  if (!valido) {
    if (canal && !estado.value.tipoOperacion) estado.value.canal = canal
    return
  }
  const aMedias = !!estado.value.tipoOperacion && estado.value.tipoOperacion !== codigo
  if (aMedias) {
    tramiteEnlace.value = codigo
    return
  }
  if (canal) estado.value.canal = canal
  if (!estado.value.tipoOperacion) {
    empezarConTramite(codigo)
    aplicarPrecarga(codigo)
  }
}

/**
 * Viene del formulario de contacto guiado (?precarga=1): los datos de contacto que ya escribió allí, para no repetirlos.
 * Solo si es el mismo trámite y de hace menos de una hora; se borran al usarlos. Ref: https://trello.com/c/63NGWLHt
 */
function aplicarPrecarga(codigo: string) {
  if (route.query.precarga !== '1' || !process.client) return
  let p: PrecargaEncargo | null = null
  try {
    p = JSON.parse(sessionStorage.getItem(PRECARGA_ENCARGO) || 'null')
    sessionStorage.removeItem(PRECARGA_ENCARGO)
  } catch { return }
  if (!p || p.tramite !== codigo || Date.now() - p.fecha > CADUCIDAD_PRECARGA_MS) return
  // Lo escribió el propio cliente: "para mí". El nombre llega en un solo campo: lo primero, nombre; el resto, apellidos
  estado.value.presentador.tipo = 'INTERESADO'
  const s = estado.value.solicitante
  const partes = (p.nombre || '').trim().split(/\s+/)
  if (!s.nombre && partes[0]) { s.nombre = partes[0]; s.apellidos = s.apellidos || partes.slice(1).join(' ') }
  s.email = s.email || p.email
  s.telefono = s.telefono || p.telefono
  if (p.presupuesto && config.value.PermitirPresupuesto) estado.value.quierePresupuesto = true
}

function empezarConEnlace() {
  const codigo = tramiteEnlace.value
  tramiteEnlace.value = null
  if (!codigo) return
  reiniciar()
  const canal = canalValido(route.query.origen)
  if (canal) estado.value.canal = canal
  empezarConTramite(codigo)
  aplicarPrecarga(codigo)
}

const enviando = ref(false)
const errorEnvio = ref(false)
const codigoError = ref('')
const referencia = ref<string | null>(null)
const enlaceCliente = ref<string | null>(null)
const respaldo = ref(false)
const docsPendientes = ref(0)
const presupuestoEnvio = ref(false)
const honeypot = ref('')
const tituloRef = ref<HTMLElement | null>(null)

const nombreNotaria = computed(() => destino.value?.nombre ?? store.NOTARIA?.Nombre ?? t('La notaría'))
/** Texto final de la plantilla cuando el encargo se queda sin notaría de destino ("sin preferencia"). */
const exitoSinDestino = computed(() =>
  encargoPlantilla.value?.pasoDestino && !destino.value ? encargoPlantilla.value.textoExitoSinDestino ?? null : null)
const siguienteSinDestino = computed(() =>
  encargoPlantilla.value?.pasoDestino && !destino.value ? encargoPlantilla.value.siguienteSinDestino ?? null : null)

const acentoWeb = computed(() => {
  let color = ''
  try { color = (styles as any).ThemeFormContact?.Button?.css?.BackgroundColor || '' } catch { /* sin estilos */ }
  return acentoLegible(color)
})
// Colores de los botones elegidos en el panel (Encargo online); lo vacío sigue a la web. Ref: https://trello.com/c/16vODf98
const boton = computed(() => estiloBotonEncargo((store.NOTARIA as any)?.SolicitudWeb?.Boton, acentoWeb.value))
const acento = computed(() => boton.value.acento)
/** El texto de los botones se lee siempre, sea cual sea el color de la notaría. */
const sobreAcento = computed(() => textoSobre(acento.value))
// Sin colores propios no se fijan las variables del botón: siguen al acento (y a lo que fije la plantilla, como minotaria)
const variablesColor = computed(() => ({
  '--sw-acento': acento.value, '--sw-sobre-acento': sobreAcento.value,
  ...(boton.value.propio
    ? { '--sw-boton-fondo': boton.value.fondo, '--sw-boton-texto': boton.value.texto, '--sw-boton-borde': boton.value.borde }
    : {}),
}))

/** Nombre corto de cada paso para la guía de pasos. */
function nombrePaso(p: string) {
  return ({
    tipo: t('Encargo'),
    destino: t('Notaría'),
    presentador: t('Para quién'),
    solicitante: esProfesional.value ? t('Cliente') : t('Sus datos'),
    intervinientes: t('Intervinientes'),
    detalle: t('Detalles'),
    revision: t('Revisión')
  } as Record<string, string>)[p] ?? p
}

/** Solo se muestra el aviso de borrador cuando hay algo que guardar. */
const hayDatos = computed(() => indice.value > 0 || !!estado.value.tipoOperacion)

/** Plazo habitual de respuesta que indica la notaría en el panel (0 = no se muestra). */
const plazoHoras = computed(() => Number(config.value.PlazoRespuestaHoras) || 0)
/** Medio de contacto elegido al enviar (el borrador se borra al terminar). */
const medioEnvio = ref('')
const textoContacto = computed(() => ({
  TELEFONO: t('Le llamaremos por teléfono'),
  EMAIL: t('Le escribiremos por email'),
  WHATSAPP: t('Le escribiremos por WhatsApp')
} as Record<string, string>)[medioEnvio.value] ?? t('Le contactaremos'))

/** Canal con el que se envió (el borrador se borra al enviar, así que se guarda aparte). */
const canalEnvio = ref<string | null>(null)

const confirmarBorrar = ref(false)
function borrarYEmpezar() {
  confirmarBorrar.value = false
  reiniciar()
}

const urlNotariaVirtual = computed(() => {
  const u = store.NOTARIA?.NotariaVirtual
  if (!conNV.value || !u) return null
  return /^https?:\/\//i.test(u) ? u : 'https://' + u
})

// minotaria.es: su correo de contacto, nunca uno de Notin. Ref: https://trello.com/c/Qa9LOQkF
const { correoEncargo } = useContactoLegal()
const contactoNotaria = computed(() => {
  const c = store.NOTARIA?.Contacto
  // Algunas plantillas (minotaria) no dan teléfono: solo el correo del panel. Ref: https://trello.com/c/pooUEbKK
  if (c?.Telefono && !encargoPlantilla.value?.sinTelefono) return { href: 'tel:' + c.Telefono, texto: c.Telefono }
  if (correoEncargo.value) return { href: 'mailto:' + correoEncargo.value, texto: correoEncargo.value }
  return null
})

const mensajeError = computed(() => {
  switch (codigoError.value) {
    case 'LIMITE': return t('Se han enviado demasiados encargos desde esta conexión. Inténtelo de nuevo dentro de una hora.')
    case 'VALIDACION': return t('Hay algún dato que no es válido. Revise los apartados y vuelva a intentarlo.')
    case 'ADJUNTO_TIPO': return t('Alguno de los archivos no es un PDF, JPG o PNG válido. Quítelo y vuelva a intentarlo.')
    case 'ADJUNTO_TAMANO':
    case 'ADJUNTOS_DEMASIADO_GRANDES':
    case 'DEMASIADOS_ADJUNTOS': return t('Los archivos adjuntos superan el tamaño permitido. Quite alguno y vuelva a intentarlo.')
    case 'NO_ACTIVA': return t('La notaría no tiene activo el encargo online en este momento.')
    case 'CITA_NO_VALIDA': return t('La fecha de la cita ya no está disponible. Vuelva a «Detalles» y elija otra.')
    default: return t('No hemos podido enviar el encargo. Sus datos siguen aquí; inténtelo de nuevo en unos minutos.')
  }
})

const tituloPaso = computed(() => ({
  presentador: t('¿Para quién es el encargo?'),
  tipo: t('¿Qué necesita?'),
  destino: videoconferenciaEn(estado.value.tipoOperacion) ? t('¿Cómo quiere firmar?') : t('Elija notaría'),
  solicitante: esProfesional.value ? t('Datos de su cliente') : t('Sus datos'),
  intervinientes: t('Otros intervinientes'),
  detalle: t('Detalles'),
  revision: t('Revise y envíe')
}[pasoActual.value]))

// Accesibilidad: al cambiar de paso, el foco va al título
watch(pasoActual, async () => {
  errorEnvio.value = false
  await nextTick()
  tituloRef.value?.focus({ preventScroll: true })
})

// reCAPTCHA v3 (mismo grupo de claves que el formulario de contacto)
let obtenerToken: (() => Promise<string>) | null = null
if (process.client && store.recaptchaSiteKey) {
  const { vueApp } = useNuxtApp()
  if (!(vueApp.config.globalProperties as any).$recaptcha) {
    vueApp.use(VueReCaptcha, { siteKey: store.recaptchaSiteKey, loaderOptions: { autoHideBadge: false } })
  }
  const rc = useReCaptcha()
  if (rc) {
    obtenerToken = async () => {
      await rc.recaptchaLoaded()
      return rc.executeRecaptcha('email')
    }
  }
}

// Estadísticas (embudo del formulario, sin datos personales)
const montado = ref(false)
watch(pasoActual, (p) => { if (p && !referencia.value && montado.value) contarPaso(p) })

onMounted(async () => {
  // Con el encargo ya enviado no se vuelve a guardar el borrador
  activarAutoguardado(() => !!referencia.value)
  cargarCatalogo()
  vigilarIdioma()
  if (!(await aplicarInvitacion())) aplicarEnlace()
  montado.value = true
  contarPaso('inicio')
  contarPaso(pasoActual.value)
})

async function onContinuar() {
  if (pasoActual.value !== 'revision') {
    siguiente()
    return
  }
  const pasoConError = validarTodo()
  if (pasoConError) {
    if (pasoConError !== 'revision') irAPaso(pasoConError)
    return
  }
  enviando.value = true
  errorEnvio.value = false
  try {
    // Con notaría de destino, el envío se firma con la clave de reCAPTCHA de su grupo. En minotaria sin notaría
    // elegida, con la del grupo de la propia web por el mismo camino (el de vue-recaptcha-v3 no llegaba a pedir token)
    const grupoFirma = destino.value ? destino.value.recaptchaGroup : (store.NOTARIA?.Personalizar?.RecaptchaGroup || 1)
    const firmar = hayTokenDestino()
      ? () => tokenDestino(grupoFirma) as Promise<string>
      : obtenerToken
    const token = firmar ? await firmar() : ''
    // Segundo token: lo usa la API si tiene que reenviar el email a la notaría sin adjuntos
    // (siempre lleva al menos el resumen en PDF)
    const tokenReintento = firmar ? await firmar() : ''
    const r = await enviar(token, honeypot.value, tokenReintento)
    canalEnvio.value = estado.value.canal ?? null
    medioEnvio.value = estado.value.contactoPreferido?.medio ?? ''
    presupuestoEnvio.value = !!(config.value.PermitirPresupuesto && estado.value.quierePresupuesto)
    enlaceCliente.value = r.enlace || null
    respaldo.value = !!r.respaldo
    docsPendientes.value = r.documentosPendientes ?? 0
    referencia.value = r.referencia
    borrarBorrador()
    if (process.client) window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (e) {
    console.error('[SolicitudWeb] Error al enviar', e)
    codigoError.value = e instanceof ErrorEnvioSolicitud ? e.codigo : 'ERROR'
    errorEnvio.value = true
  } finally {
    enviando.value = false
  }
}

// El estado del formulario es compartido en la web (useState): si sale con el encargo ya enviado, se limpia
// para que al volver a entrar empiece de cero y no vea el encargo que ya mandó
onBeforeUnmount(() => {
  if (referencia.value) reiniciar()
})

function nuevaSolicitud() {
  const canal = canalEnvio.value
  referencia.value = null
  enlaceCliente.value = null
  respaldo.value = false
  docsPendientes.value = 0
  presupuestoEnvio.value = false
  reiniciar()
  // Si entró por el QR de la oficina, la siguiente también cuenta como hecha en la notaría
  if (canal) estado.value.canal = canal
}
</script>

<style lang="scss">
/* Estilos sin scope: los comparten los componentes de cada paso (prefijo .sw) */
.sw {
  --sw-acento: #1f3a5f;
  --sw-texto: #1d2329;
  --sw-tenue: #5f6b76;
  --sw-borde: #d9dee3;
  --sw-fondo-suave: #f6f7f8;
  padding: 48px 16px 72px;
  color: var(--sw-texto);

  &__contenedor { max-width: 760px; margin: 0 auto; }

  &__antetitulo {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 0.9rem; font-weight: 600; color: var(--sw-acento); margin-bottom: 6px;
  }
  &__titulo {
    font-size: clamp(1.6rem, 4vw, 2.2rem); line-height: 1.2; font-weight: 600;
    margin: 0 0 20px; outline: none;
  }
  &__progreso {
    height: 4px; background: var(--sw-borde); border-radius: 2px; overflow: hidden;
  }
  &__progreso-barra {
    height: 100%; background: var(--sw-acento); transition: width 0.3s ease;
  }
  &__paso { font-size: 0.85rem; color: var(--sw-tenue); margin: 10px 0 28px; }

  // Guía de pasos con nombre (en móvil solo se ve el nombre del paso actual, debajo)
  &__pasos { list-style: none; padding: 0; margin: 4px 0 0; display: flex; gap: 6px; }
  &__pasos-item {
    flex: 1 1 0; min-width: 0; border-top: 4px solid var(--sw-borde); padding-top: 8px;
    &--hecho, &--actual { border-top-color: var(--sw-acento); }
  }
  &__pasos-boton {
    display: flex; align-items: center; gap: 6px; width: 100%; min-height: 32px;
    background: none; border: 0; padding: 0; font: inherit; color: var(--sw-tenue); text-align: start;
  }
  button.sw__pasos-boton { cursor: pointer; &:hover .sw__pasos-nombre { text-decoration: underline; } }
  button.sw__pasos-boton:focus-visible { outline: 3px solid var(--sw-acento); outline-offset: 2px; border-radius: 4px; }
  &__pasos-num {
    flex: 0 0 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.75rem; font-weight: 700; border: 1.5px solid var(--sw-borde); background: #fff; color: var(--sw-tenue);
  }
  &__pasos-item--hecho &__pasos-num, &__pasos-item--actual &__pasos-num {
    background: var(--sw-acento); border-color: var(--sw-acento); color: var(--sw-sobre-acento, #fff);
    .v-icon { color: var(--sw-sobre-acento, #fff) !important; }
  }
  &__pasos-item--actual &__pasos-boton { color: var(--sw-texto); font-weight: 600; }
  &__pasos-nombre { font-size: 0.82rem; line-height: 1.25; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media (max-width: 700px) {
    &__pasos-nombre { display: none; }
    &__pasos-boton { justify-content: center; }
  }
  @media (min-width: 701px) {
    &__paso { display: none; }
    &__pasos { margin-bottom: 28px; }
  }

  &__acciones {
    display: flex; align-items: center; gap: 12px;
    margin-top: 32px; padding-top: 20px; border-top: 1px solid var(--sw-borde);
  }
  &__conflicto {
    display: flex; gap: 12px; align-items: flex-start; margin: 0 0 24px; padding: 16px 18px;
    border: 1.5px solid var(--sw-acento); border-radius: 12px; background: color-mix(in srgb, var(--sw-acento) 5%, #fff);
    p { margin: 0 0 12px; font-weight: 600; }
    .v-icon { color: var(--sw-acento) !important; margin-top: 2px; }
  }
  &__conflicto-acciones { display: flex; flex-wrap: wrap; gap: 10px; .v-btn { text-transform: none; letter-spacing: 0; } }
  &__pie { margin-top: 24px; font-size: 0.85rem; color: var(--sw-tenue); display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
  &__pie-icono { color: #2e7d32 !important; }
  &__pie-confirmar { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-inline-start: 6px; color: var(--sw-texto); }

  &__aviso {
    display: flex; gap: 10px; align-items: flex-start; margin-top: 24px; padding: 14px 16px;
    border-radius: 8px; background: #fdecec; color: #8a1c1c; font-size: 0.95rem;
    a { color: inherit; font-weight: 600; }
  }

  &__hp { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }

  &__exito {
    text-align: center; padding: 40px 0;
    p { max-width: 560px; margin: 0 auto 12px; }
  }
  &__siguientes {
    max-width: 460px; margin: 24px auto 8px; text-align: start;
    ol { list-style: none; padding: 0; margin: 0; display: grid; gap: 14px; }
    li { display: flex; gap: 12px; align-items: flex-start; }
    li div { display: flex; flex-direction: column; gap: 2px; }
    li span:not(.sw__siguientes-num) { color: var(--sw-tenue); font-size: 0.92rem; }
    li.hecho .sw__siguientes-num { background: #2e7d32; border-color: #2e7d32; color: #fff; .v-icon { color: #fff !important; } }
  }
  &__siguientes-titulo { font-weight: 700; margin: 0 0 12px; }
  &__siguientes-num {
    flex: 0 0 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
    border: 2px solid var(--sw-acento); color: var(--sw-acento); font-weight: 700; font-size: 0.85rem;
  }
  &__exito-qr {
    display: inline-flex; align-items: center; gap: 8px; margin: 4px auto 0; padding: 10px 16px; border-radius: 10px;
    background: color-mix(in srgb, var(--sw-acento) 8%, #fff); font-weight: 600;
    .v-icon { color: var(--sw-acento) !important; }
  }
  &__exito-icono { font-size: 64px !important; color: var(--sw-acento) !important; margin-bottom: 16px; }
  &__referencia { font-size: 1.1rem; }
  &__exito-acciones { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; margin-top: 28px; }
}

.sw-btn-principal.v-btn {
  background: var(--sw-boton-fondo, var(--sw-acento)) !important; color: var(--sw-boton-texto, var(--sw-sobre-acento, #fff)) !important;
  border: 2px solid var(--sw-boton-borde, var(--sw-boton-fondo, var(--sw-acento))) !important;
  text-transform: none; letter-spacing: 0; font-weight: 600; padding: 0 24px; height: 48px;
}
.sw .v-btn { text-transform: none; letter-spacing: 0; }

.sw-intro { font-size: 1.02rem; line-height: 1.6; color: var(--sw-tenue); margin-bottom: 24px; }
.sw-subtitulo { font-weight: 600; margin: 20px 0 10px; }
.sw-ayuda { font-size: 0.88rem; color: var(--sw-tenue); margin-top: 4px; }
.sw-error { color: rgb(var(--v-theme-error)); font-size: 0.88rem; margin-top: 10px; }
.sw-tenue { color: var(--sw-tenue); font-size: 0.92rem; }
.sw-prewrap { white-space: pre-wrap; }
.sw-bloque { margin-top: 28px; }

.sw-editar {
  background: none; border: 0; padding: 0; color: var(--sw-acento); font-weight: 600;
  text-decoration: underline; text-underline-offset: 2px; cursor: pointer; font-size: inherit;
  &--peligro { color: #b3261e; }
}

/* Tarjetas de opción */
.sw-opciones {
  display: grid; gap: 12px;
  &--2 { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
  &--tipos { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
}
.sw-opcion {
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px; text-align: left;
  padding: 18px; border: 1.5px solid var(--sw-borde); border-radius: 10px; background: #fff;
  cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  &:hover { border-color: var(--sw-acento); }
  &:focus-visible { outline: 3px solid var(--sw-acento); outline-offset: 2px; }
  &.activo {
    border-color: var(--sw-acento);
    box-shadow: inset 0 0 0 1px var(--sw-acento);
    background: color-mix(in srgb, var(--sw-acento) 6%, #fff);
  }
  &__icono { color: var(--sw-acento) !important; font-size: 26px !important; }
  &__titulo { font-weight: 600; font-size: 1rem; }
  &__texto { font-size: 0.88rem; color: var(--sw-tenue); line-height: 1.45; }
}

/* Selector física / jurídica */
.sw__invitacion {
  display: flex; gap: 10px; align-items: flex-start; margin: 0 0 20px; padding: 12px 14px; border-radius: 10px;
  background: color-mix(in srgb, var(--sw-acento) 7%, #fff);
  p { margin: 0; }
  .v-icon { color: var(--sw-acento) !important; margin-top: 1px; }
  &--aviso { background: #fff4e5; .v-icon { color: #b26a00 !important; } }
}
.sw__whatsapp {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 20px; font-size: 0.9rem; color: var(--sw-tenue);
  .v-icon { color: #25d366 !important; }
  a { color: var(--sw-acento) !important; font-weight: 600; }
}
.sw__asistente {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 12px; font-size: 0.9rem; color: var(--sw-tenue);
  .v-icon { color: var(--sw-acento) !important; }
}
.sw__asistente-boton {
  background: none; border: 0; padding: 0; cursor: pointer; font: inherit; font-weight: 600; text-decoration: underline;
  color: var(--sw-acento) !important;
}
.sw__seguimiento {
  display: flex; gap: 10px; align-items: flex-start; text-align: start; margin: 20px auto 0; max-width: 520px;
  padding: 12px 14px; border-radius: 10px; background: rgba(0, 0, 0, 0.04);
  p { margin: 0; font-size: 0.95rem; }
  .v-icon { color: var(--sw-acento); margin-top: 2px; }
}
.sw-preferencias { display: flex; flex-wrap: wrap; gap: 0 16px; }
.sw-presupuesto { margin-top: 16px; padding: 4px 12px; border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 10px; }
.sw-segmento--envolver { flex-wrap: wrap; button { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; } }
.sw-segmento {
  display: inline-flex; border: 1.5px solid var(--sw-borde); border-radius: 8px; overflow: hidden;
  margin-bottom: 18px;
  button {
    padding: 8px 16px; background: #fff; border: 0; cursor: pointer; font-size: 0.92rem;
    & + button { border-left: 1.5px solid var(--sw-borde); }
    &.activo { background: var(--sw-acento); color: var(--sw-sobre-acento, #fff); font-weight: 600; .v-icon { color: inherit !important; } }
    &:focus-visible { outline: 3px solid var(--sw-acento); outline-offset: -3px; }
  }
}

.sw-tarjeta {
  border: 1.5px solid var(--sw-borde); border-radius: 10px; padding: 16px 16px 4px; margin-bottom: 16px;
  &__cabecera { display: flex; justify-content: space-between; align-items: center;
    .sw-subtitulo { margin: 0; } }
}
.sw-anadir { margin-top: 4px; }

.sw-documentos {
  margin-top: 24px; padding: 16px 20px; background: var(--sw-fondo-suave); border-radius: 10px;
  .sw-subtitulo { margin-top: 0; }
  ul { list-style: none; padding: 0; margin: 12px 0 0; }
  li { display: flex; gap: 10px; align-items: center; padding: 6px 0; }
  .v-icon { color: var(--sw-acento); }
}
.sw-doc { display: block !important; padding: 8px 0 !important; border-bottom: 1px solid var(--sw-borde);
  &:last-child { border-bottom: 0; } }
.sw-doc__fila { display: flex; gap: 10px; align-items: center; }
.sw-documentos__cabecera { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 8px; }
.sw-contador {
  font-size: 0.82rem; font-weight: 600; padding: 3px 10px; border-radius: 999px;
  background: #fff; border: 1px solid var(--sw-borde); color: var(--sw-tenue);
  &--completo { color: #1b5e20; border-color: #a5d6a7; background: #e8f5e9; }
}
.sw-adjuntar.v-btn { text-transform: none; letter-spacing: 0; color: var(--sw-acento) !important; min-height: 40px; }
.sw-doc__nombre { line-height: 1.35; }
.sw-archivo {
  display: flex; gap: 10px; align-items: center; margin: 6px 0 0 28px; padding: 4px 4px 4px 12px;
  background: #fff; border: 1px solid var(--sw-borde); border-radius: 6px; font-size: 0.9rem;
  &__nombre { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}
.sw-oculto { display: none; }
.sw-sugerencia .v-messages__message { color: rgb(var(--v-theme-warning)); }
.sw-corregir { font-size: 0.85rem; margin: -4px 0 8px 16px; display: inline-block; }
.sw-cargando { display: flex; align-items: center; color: var(--sw-tenue); padding: 24px 0; }
.sw-etiqueta {
  font-size: 0.75rem; padding: 2px 8px; border-radius: 10px; background: #fff;
  border: 1px solid var(--sw-borde); color: var(--sw-tenue);
}

/* Resumen */
.sw-resumen {
  margin: 0;
  &__fila {
    display: grid; grid-template-columns: 170px 1fr auto; gap: 8px 16px; align-items: start;
    padding: 16px 0; border-bottom: 1px solid var(--sw-borde);
    dt { font-weight: 600; }
    dd { margin: 0; }
  }
}
@media (max-width: 600px) {
  .sw { padding-top: 32px; }
  .sw-resumen__fila { grid-template-columns: 1fr auto; dd { grid-column: 1 / -1; grid-row: 2; } }
}

.sw-rgpd {
  margin-top: 28px; padding: 16px 20px; background: var(--sw-fondo-suave); border-radius: 10px;
  p { font-size: 0.82rem; line-height: 1.55; color: var(--sw-tenue); margin-bottom: 8px; }
  a { color: var(--sw-acento); font-weight: 600; }
}
</style>
